import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Loader2, BookOpen, FileText, Image as ImageIcon, Pencil, Save, Trash2,
  Upload, X, Plus, Zap, CheckCircle2, XCircle, HelpCircle, ArrowRight, ArrowLeft, Wand2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";

type SourceType = "lesson" | "pdf" | "image" | "text";

interface Question {
  question_text: string;
  question_type: "multiple_choice" | "true_false" | "essay";
  difficulty: "easy" | "medium" | "hard";
  options?: string[];
  correct_answer: string;
  explanation?: string;
}

interface Props {
  classes: { id: string; name: string }[];
  onSuccess: () => void;
}

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const MAX_FILES = 10;

const diffColors: Record<string, string> = {
  easy: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  hard: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};
const diffLabels: Record<string, string> = { easy: "سهل", medium: "متوسط", hard: "صعب" };
const typeLabels: Record<string, string> = { multiple_choice: "اختيار من متعدد", true_false: "صح وخطأ", essay: "مقالي" };
const typeIcons: Record<string, React.ReactNode> = {
  multiple_choice: <CheckCircle2 className="w-3.5 h-3.5" />,
  true_false: <XCircle className="w-3.5 h-3.5" />,
  essay: <HelpCircle className="w-3.5 h-3.5" />,
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SmartExamWizard({ classes, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const nurseryId = profile?.nursery_id;

  // Step 1
  const [source, setSource] = useState<SourceType | null>(null);

  // Step 2
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [pastedText, setPastedText] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 3 settings
  const [questionCount, setQuestionCount] = useState("5");
  const [difficulty, setDifficulty] = useState("mixed");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["multiple_choice", "true_false"]);
  const [generating, setGenerating] = useState(false);

  // Step 4
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [examTitle, setExamTitle] = useState("");
  const [classId, setClassId] = useState("");
  const [duration, setDuration] = useState("15");
  const [saving, setSaving] = useState(false);

  // Fetch lessons for "from lesson" source
  const { data: lessons } = useQuery({
    queryKey: ["lessons-for-wizard", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lessons")
        .select("id, title, content")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: open && !!nurseryId && source === "lesson",
  });

  const selectedLesson = (lessons || []).find((l: any) => l.id === selectedLessonId);

  // File handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addFiles = (files: File[]) => {
    const acceptTypes = source === "image"
      ? ["image/jpeg", "image/png", "image/webp"]
      : ["application/pdf"];
    const validFiles: File[] = [];
    for (const file of files) {
      if (uploadedFiles.length + validFiles.length >= MAX_FILES) {
        toast({ title: "تنبيه", description: `الحد الأقصى ${MAX_FILES} ملفات`, variant: "destructive" });
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast({ title: "خطأ", description: `${file.name} أكبر من 15MB`, variant: "destructive" });
        continue;
      }
      if (!acceptTypes.includes(file.type) && !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        toast({ title: "خطأ", description: `${file.name} نوع غير مدعوم`, variant: "destructive" });
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length > 0) setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [uploadedFiles.length, source]);

  const removeFile = (index: number) => setUploadedFiles(prev => prev.filter((_, i) => i !== index));

  const toggleType = (type: string) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // Can proceed to step 3?
  const canProceedToGenerate = () => {
    if (source === "lesson") return !!selectedLessonId;
    if (source === "pdf" || source === "image") return uploadedFiles.length > 0;
    if (source === "text") return pastedText.trim().length > 10;
    return false;
  };

  // Generate questions via AI
  const generateQuestions = async () => {
    if (selectedTypes.length === 0) {
      toast({ title: "خطأ", description: "اختر نوع سؤال واحد على الأقل", variant: "destructive" });
      return;
    }
    setGenerating(true);
    try {
      const body: Record<string, any> = {
        questionCount: parseInt(questionCount),
        difficulty,
        questionTypes: selectedTypes,
      };

      if (source === "lesson" && selectedLesson) {
        body.lessonTitle = selectedLesson.title;
        body.lessonContent = selectedLesson.content || "";
      } else if (source === "text") {
        body.lessonTitle = "امتحان من نص";
        body.lessonContent = pastedText;
      } else if ((source === "pdf" || source === "image") && uploadedFiles.length > 0) {
        body.lessonTitle = "امتحان من ملفات";
        const filesData = await Promise.all(
          uploadedFiles.map(async (file) => ({
            base64: await fileToBase64(file),
            mimeType: file.type,
            name: file.name,
          }))
        );
        body.files = filesData;
        if (filesData.length === 1) {
          body.fileBase64 = filesData[0].base64;
          body.fileMimeType = filesData[0].mimeType;
          body.fileName = filesData[0].name;
        }
      }

      const { data, error } = await supabase.functions.invoke("generate-questions", { body });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const qs = data.questions || [];
      setQuestions(qs);
      setExamTitle(
        source === "lesson" && selectedLesson
          ? `اختبار: ${selectedLesson.title}`
          : `امتحان ${new Date().toLocaleDateString("ar-EG")}`
      );
      setStep(4);
      toast({ title: `✨ تم توليد ${qs.length} أسئلة بنجاح` });
    } catch (e: any) {
      console.error("Generate error:", e);
      toast({ title: "خطأ", description: e.message || "فشل في توليد الأسئلة", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  // Save exam
  const handleSave = async () => {
    if (questions.length === 0 || !examTitle.trim()) return;
    setSaving(true);
    try {
      // Save questions to bank
      const rows = questions.map((q) => ({
        nursery_id: nurseryId!,
        lesson_id: source === "lesson" ? selectedLessonId || null : null,
        question_text: q.question_text,
        question_type: q.question_type,
        difficulty: q.difficulty,
        options: q.options || null,
        correct_answer: q.correct_answer,
        explanation: q.explanation || null,
        is_ai_generated: true,
      }));

      const { data: savedQs, error: qError } = await supabase.from("questions").insert(rows).select("id");
      if (qError) throw qError;

      // Create exam
      const { data: exam, error: examError } = await supabase.from("exams").insert({
        title: examTitle.trim(),
        nursery_id: nurseryId!,
        class_id: classId || null,
        lesson_id: source === "lesson" ? selectedLessonId || null : null,
        duration_minutes: parseInt(duration) || 15,
        max_score: savedQs.length,
        is_interactive: true,
      }).select("id").single();
      if (examError) throw examError;

      const examQuestions = savedQs.map((q, i) => ({
        exam_id: exam.id,
        question_id: q.id,
        sort_order: i,
        points: 1,
      }));
      const { error: linkError } = await supabase.from("exam_questions").insert(examQuestions);
      if (linkError) throw linkError;

      toast({ title: `🎉 تم إنشاء امتحان "${examTitle}" مع ${savedQs.length} سؤال` });
      resetForm();
      setOpen(false);
      onSuccess();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeQuestion = (index: number) => setQuestions(prev => prev.filter((_, i) => i !== index));
  const updateQuestion = (index: number, updated: Partial<Question>) => {
    setQuestions(prev => prev.map((q, i) => (i === index ? { ...q, ...updated } : q)));
  };

  const resetForm = () => {
    setStep(1);
    setSource(null);
    setSelectedLessonId("");
    setPastedText("");
    setUploadedFiles([]);
    setQuestionCount("5");
    setDifficulty("mixed");
    setSelectedTypes(["multiple_choice", "true_false"]);
    setQuestions([]);
    setEditIndex(null);
    setExamTitle("");
    setClassId("");
    setDuration("15");
  };

  const sourceCards: { type: SourceType; icon: React.ReactNode; label: string; desc: string; emoji: string }[] = [
    { type: "lesson", icon: <BookOpen className="w-8 h-8" />, label: "من درس موجود", desc: "اختر درس من المنهج", emoji: "📘" },
    { type: "pdf", icon: <FileText className="w-8 h-8" />, label: "من ملف PDF", desc: "ارفع ملف PDF", emoji: "📄" },
    { type: "image", icon: <ImageIcon className="w-8 h-8" />, label: "من صورة", desc: "ارفع صورة كتاب أو ورقة", emoji: "🖼️" },
    { type: "text", icon: <Pencil className="w-8 h-8" />, label: "كتابة / لصق", desc: "اكتب أو الصق المحتوى", emoji: "✍️" },
  ];

  const stepLabels = ["اختر المصدر", "أدخل المحتوى", "إعدادات التوليد", "مراجعة ونشر"];

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-l from-primary to-primary/80 shadow-lg">
          <Wand2 className="w-4 h-4 ml-2" />
          ✨ إنشاء امتحان ذكي
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-primary" />
            إنشاء امتحان ذكي
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-2 mb-2">
          {stepLabels.map((label, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                step > i + 1 ? "bg-green-500 text-white" :
                step === i + 1 ? "bg-primary text-primary-foreground" :
                "bg-muted text-muted-foreground"
              }`}>
                {step > i + 1 ? "✓" : i + 1}
              </div>
              <span className={`text-[10px] hidden sm:inline ${step === i + 1 ? "text-primary font-bold" : "text-muted-foreground"}`}>
                {label}
              </span>
              {i < 3 && <div className={`w-6 sm:w-10 h-0.5 ${step > i + 1 ? "bg-green-500" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Choose Source */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-gradient-to-l from-primary/10 to-primary/5 rounded-xl p-4 text-center space-y-1">
              <div className="text-3xl">🎯</div>
              <p className="font-bold text-sm">اختر مصدر المحتوى</p>
              <p className="text-xs text-muted-foreground">المعلم يضغط زر بس… والامتحان جاهز 🔥</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {sourceCards.map((s) => (
                <motion.div
                  key={s.type}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`border-2 rounded-xl p-4 cursor-pointer text-center transition-colors space-y-2 ${
                    source === s.type ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                  }`}
                  onClick={() => { setSource(s.type); setStep(2); }}
                >
                  <div className="text-3xl">{s.emoji}</div>
                  <p className="font-bold text-sm">{s.label}</p>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Content Input */}
        {step === 2 && (
          <ScrollArea className="flex-1 max-h-[55vh]">
            <div className="space-y-4 pl-2">
              {source === "lesson" && (
                <div className="space-y-3">
                  <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-3 text-center">
                    <p className="text-sm font-medium">📘 اختر درس من المنهج</p>
                  </div>
                  <Select value={selectedLessonId} onValueChange={setSelectedLessonId}>
                    <SelectTrigger><SelectValue placeholder="اختر درس..." /></SelectTrigger>
                    <SelectContent>
                      {(lessons || []).map((l: any) => (
                        <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedLesson && (
                    <div className="bg-muted/50 rounded-lg p-3 text-sm max-h-32 overflow-y-auto">
                      <p className="text-xs text-muted-foreground mb-1">محتوى الدرس:</p>
                      {selectedLesson.content ? selectedLesson.content.substring(0, 300) + "..." : <span className="text-muted-foreground italic">لا يوجد محتوى نصي</span>}
                    </div>
                  )}
                </div>
              )}

              {(source === "pdf" || source === "image") && (
                <div className="space-y-3">
                  <div className={`${source === "pdf" ? "bg-red-50 dark:bg-red-900/10" : "bg-purple-50 dark:bg-purple-900/10"} rounded-xl p-3 text-center`}>
                    <p className="text-sm font-medium">{source === "pdf" ? "📄 ارفع ملفات PDF" : "🖼️ ارفع صور"}</p>
                    <p className="text-xs text-muted-foreground">الذكاء الاصطناعي سيحلل المحتوى ويولّد أسئلة منه</p>
                  </div>

                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                      {uploadedFiles.map((file, index) => {
                        const isImage = file.type.startsWith("image/");
                        return (
                          <motion.div
                            key={`${file.name}-${index}`}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 bg-muted/50 rounded-xl p-3 border"
                          >
                            {isImage ? (
                              <div className="w-12 h-12 rounded-lg overflow-hidden border bg-muted shrink-0">
                                <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <FileText className="w-10 h-10 text-red-500 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                            </div>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => removeFile(index)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {uploadedFiles.length < MAX_FILES && (
                    <div
                      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                        isDragging ? "border-primary bg-primary/10" : "border-muted-foreground/25 hover:border-primary/50"
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                    >
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">اسحب وأفلت أو اضغط للرفع</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {source === "pdf" ? "PDF" : "JPG, PNG, WEBP"} — حتى 15MB — حتى {MAX_FILES} ملفات
                      </p>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={source === "pdf" ? ".pdf" : ".jpg,.jpeg,.png,.webp"}
                    className="hidden"
                    onChange={handleFileSelect}
                    multiple
                  />
                </div>
              )}

              {source === "text" && (
                <div className="space-y-3">
                  <div className="bg-orange-50 dark:bg-orange-900/10 rounded-xl p-3 text-center">
                    <p className="text-sm font-medium">✍️ اكتب أو الصق المحتوى</p>
                  </div>
                  <Textarea
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="الصق محتوى الدرس أو الموضوع هنا..."
                    className="min-h-[150px]"
                  />
                  <p className="text-xs text-muted-foreground text-left">{pastedText.length} حرف</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setStep(1); setUploadedFiles([]); setPastedText(""); setSelectedLessonId(""); }}>
                  <ArrowRight className="w-4 h-4 ml-1" /> رجوع
                </Button>
                <Button
                  className="flex-1"
                  disabled={!canProceedToGenerate()}
                  onClick={() => setStep(3)}
                >
                  التالي: إعدادات التوليد <ArrowLeft className="w-4 h-4 mr-1" />
                </Button>
              </div>
            </div>
          </ScrollArea>
        )}

        {/* Step 3: Generation Settings */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-gradient-to-l from-primary/10 to-primary/5 rounded-xl p-4 text-center space-y-1">
              <div className="text-3xl">🤖</div>
              <p className="font-bold text-sm">إعدادات التوليد بالذكاء الاصطناعي</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>عدد الأسئلة</Label>
                <Select value={questionCount} onValueChange={setQuestionCount}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["3", "5", "7", "10", "15", "20"].map((n) => (
                      <SelectItem key={n} value={n}>{n} أسئلة</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>مستوى الصعوبة</Label>
                <Select value={difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mixed">🎯 متنوع</SelectItem>
                    <SelectItem value="easy">🟢 سهل</SelectItem>
                    <SelectItem value="medium">🟡 متوسط</SelectItem>
                    <SelectItem value="hard">🔴 صعب</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">أنواع الأسئلة</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: "multiple_choice", label: "اختيار من متعدد" },
                  { value: "true_false", label: "صح وخطأ" },
                  { value: "essay", label: "مقالي" },
                ].map((t) => (
                  <label key={t.value} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={selectedTypes.includes(t.value)} onCheckedChange={() => toggleType(t.value)} />
                    <span className="text-sm">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowRight className="w-4 h-4 ml-1" /> رجوع
              </Button>
              <Button
                className="flex-1 h-12 text-base rounded-xl bg-gradient-to-l from-primary to-primary/80 shadow-lg"
                disabled={generating || selectedTypes.length === 0}
                onClick={generateQuestions}
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 animate-spin ml-2" />جاري التوليد...</>
                ) : (
                  <><Sparkles className="w-5 h-5 ml-2" />✨ توليد الأسئلة</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Review & Publish */}
        {step === 4 && (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            {/* Exam settings */}
            <div className="bg-gradient-to-l from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-xl p-3 border border-green-200 dark:border-green-800 space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                <span className="font-bold text-sm text-green-700 dark:text-green-400">إعدادات الامتحان</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">عنوان الامتحان</Label>
                  <Input value={examTitle} onChange={(e) => setExamTitle(e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">المدة</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["5", "10", "15", "20", "30", "45"].map((d) => (
                        <SelectItem key={d} value={d}>{d} د</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {classes.length > 0 && (
                <div>
                  <Label className="text-xs">الفصل</Label>
                  <Select value={classId} onValueChange={setClassId}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
                    <SelectContent>
                      {classes.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">✏️ {questions.length} سؤال — راجع وعدّل:</p>
              <div className="flex gap-1 flex-wrap">
                {questions.map((q, i) => (
                  <Badge key={i} variant="outline" className={`text-[10px] ${diffColors[q.difficulty]}`}>
                    {diffLabels[q.difficulty]}
                  </Badge>
                ))}
              </div>
            </div>

            <ScrollArea className="flex-1 max-h-[30vh]">
              <div className="space-y-2 pl-2">
                {questions.map((q, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border rounded-xl p-3 space-y-1.5 bg-card"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-muted-foreground bg-muted rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {editIndex === idx ? (
                          <div className="space-y-2">
                            <Textarea
                              value={q.question_text}
                              onChange={(e) => updateQuestion(idx, { question_text: e.target.value })}
                              className="text-sm min-h-[60px]"
                            />
                            {q.options && (
                              <div className="space-y-1">
                                {q.options.map((opt, oi) => (
                                  <Input
                                    key={oi}
                                    value={opt}
                                    onChange={(e) => {
                                      const newOptions = [...q.options!];
                                      newOptions[oi] = e.target.value;
                                      updateQuestion(idx, { options: newOptions });
                                    }}
                                    className="h-8 text-sm"
                                  />
                                ))}
                              </div>
                            )}
                            <Input
                              value={q.correct_answer}
                              onChange={(e) => updateQuestion(idx, { correct_answer: e.target.value })}
                              className="h-8 text-sm"
                              placeholder="الإجابة الصحيحة"
                            />
                            <Button size="sm" onClick={() => setEditIndex(null)} className="gap-1">
                              <Save className="w-3 h-3" /> حفظ
                            </Button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium">{q.question_text}</p>
                            {q.options && (
                              <div className="grid grid-cols-2 gap-1">
                                {q.options.map((opt, oi) => (
                                  <div
                                    key={oi}
                                    className={`text-xs px-2 py-1 rounded-lg border ${
                                      opt === q.correct_answer
                                        ? "bg-green-50 border-green-300 text-green-800 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700"
                                        : "bg-muted/30"
                                    }`}
                                  >
                                    {opt === q.correct_answer && "✅ "}{opt}
                                  </div>
                                ))}
                              </div>
                            )}
                            {q.question_type === "true_false" && (
                              <p className="text-xs text-green-600 dark:text-green-400">✅ {q.correct_answer}</p>
                            )}
                            {q.question_type === "essay" && (
                              <p className="text-xs text-muted-foreground">💡 {q.correct_answer}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Badge variant="outline" className="text-[10px] gap-0.5">
                          {typeIcons[q.question_type]}
                          {typeLabels[q.question_type]}
                        </Badge>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditIndex(editIndex === idx ? null : idx)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeQuestion(idx)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setQuestions([]); setStep(3); }}>
                <ArrowRight className="w-4 h-4 ml-1" /> إعادة التوليد
              </Button>
              <Button
                className="flex-1"
                disabled={saving || questions.length === 0 || !examTitle.trim()}
                onClick={handleSave}
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin ml-2" />جاري النشر...</>
                ) : (
                  <><Zap className="w-4 h-4 ml-2" />🚀 نشر الامتحان ({questions.length} سؤال)</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
